import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { BookOpen, ExternalLink, Plus, Search } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/apiClient';
import { FILE_SERVER_URL } from '../config/api';
import { CollectionFile, CollectionSong, SONG_COLLECTIONS, isInCollection } from '../types/songCollections';
import CollectionRating from '../components/CollectionRating';
import './SongCollectionsPage.css';

const fileUrl = (file: CollectionFile) => `${FILE_SERVER_URL}/files/${encodeURIComponent(file.filename)}`;

const scoreExtensions = ['pdf', 'png', 'jpg', 'jpeg', 'tif', 'tiff'];
const hasScore = (song: CollectionSong) => song.versions.some(version =>
  version.files.some(file => scoreExtensions.includes(file.extension.toLowerCase())));
const hasArchive = (song: CollectionSong) => song.versions.some(version =>
  version.files.some(file => file.extension.toLowerCase() === 'zip'));
const seasons = ['Advent', 'Christmas', 'Epiphany', 'Lent', 'Ash Wednesday', 'Holy Week', 'Tenebrae', 'Easter', 'Trinity', 'Transfiguration', 'St Francis'];

const SongCollectionsPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const requestedCollection = params.get('collection');
  const collection = SONG_COLLECTIONS.find(item => item.id === requestedCollection);
  const [songs, setSongs] = useState<CollectionSong[]>([]);
  const [ratings, setRatings] = useState<Record<number, number | null>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [query, setQuery] = useState('');
  const [season, setSeason] = useState('');
  const [availability, setAvailability] = useState('');
  const [preview, setPreview] = useState<{ title: string; file: CollectionFile } | null>(null);
  const { user } = useAuth();
  const isAdmin = user?.role_name?.toLowerCase() === 'admin';

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError('');
    Promise.all([
      apiClient.get<{ songs: CollectionSong[] }>('/songs/collections'),
      apiClient.get<{ ratings: { song_id: number; rating: number }[] }>('/songs/collections/ratings'),
    ])
      .then(([response, personal]) => {
        if (current) {
          setSongs(response.data.songs);
          setRatings(Object.fromEntries(personal.data.ratings.map(item => [item.song_id, item.rating])));
        }
      })
      .catch(() => { if (current) setError('Could not load your collections. Please try again.'); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [retry]);

  const visible = useMemo(() => {
    const words = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
    return songs.filter(song => {
      if (collection && !isInCollection(song.tags, collection.tag)) return false;
      if (season && !song.tags.includes(season)) return false;
      if (availability === 'scores' && !hasScore(song)) return false;
      if (availability === 'sources' && (hasScore(song) || !hasArchive(song))) return false;
      const text = [song.title, song.composer_name, ...song.tags,
        ...song.versions.map(version => [version.title, version.arrangement_type, version.key_signature].join(' '))]
        .join(' ').toLocaleLowerCase();
      return words.every(word => text.includes(word));
    });
  }, [songs, collection, query, season, availability]);

  const selectCollection = (id?: string) => {
    setParams({ tab: 'collections', ...(id ? { collection: id } : {}) });
    setQuery('');
    setSeason('');
    setAvailability('');
  };
  const addUrl = `/browse?tab=songs&new=1${collection ? `&collection=${collection.id}` : ''}`;

  const renderVersion = (song: CollectionSong, version: CollectionSong['versions'][number]) => (
    <section className="song-collections__version" key={version.version_id} aria-label={version.title || 'Main version'}>
      <h3>{version.title || 'Main'}</h3>
      {(version.arrangement_type || version.key_signature) && <p className="small text-muted mb-2">
        {[version.arrangement_type, version.key_signature].filter(Boolean).join(' · ')}
      </p>}
      {version.files.length ? <ul className="song-collections__files">
        {version.files.map(file => {
          const archive = file.extension.toLowerCase() === 'zip';
          return <li key={file.file_id}>
            <a href={fileUrl(file)} target="_blank" rel="noreferrer" download={archive || undefined}>
              <ExternalLink size={15} aria-hidden="true" />
              {archive ? (hasScore(song) ? 'Download original files' : 'Download editable files') :
                file.file_type === 'choir' ? 'Choir score' :
                file.file_type === 'bulletin' ? 'Congregation score' :
                file.file_type === 'misc' ? 'Reference score' : `${file.file_type.charAt(0).toUpperCase()}${file.file_type.slice(1)} score`}
              <span className="small text-muted"> {file.extension.toUpperCase()}</span>
            </a>
            {['pdf', 'png', 'jpg', 'jpeg'].includes(file.extension.toLowerCase()) &&
              <button type="button" className="btn btn-sm btn-outline-secondary"
                onClick={() => setPreview({ title: song.title, file })}>Preview</button>}
          </li>;
        })}
      </ul> : <p className="small text-muted mb-0">No score attached yet.</p>}
    </section>
  );

  return (
    <main className="song-collections">
      <header className="song-collections__header">
        <div>
          <p className="song-collections__eyebrow">SONG COLLECTIONS</p>
          <h1>Chants &amp; Circle Songs</h1>
          <p className="text-muted mb-0">A place to return to the music we sing together.</p>
        </div>
        {isAdmin && <Link className="btn btn-primary d-inline-flex align-items-center gap-2" to={addUrl}>
          <Plus size={18} aria-hidden="true" /> Add a song
        </Link>}
      </header>

      <nav className="song-collections__filters" aria-label="Song collections">
        <button type="button" className={!collection ? 'is-selected' : ''} aria-pressed={!collection}
          onClick={() => selectCollection()}>All collections {!loading && !error && <span>{songs.length}</span>}</button>
        {SONG_COLLECTIONS.map(item => <button type="button" key={item.id}
          className={collection?.id === item.id ? 'is-selected' : ''}
          aria-pressed={collection?.id === item.id} onClick={() => selectCollection(item.id)}>
          {item.label} {!loading && !error && <span>{songs.filter(song => isInCollection(song.tags, item.tag)).length}</span>}
        </button>)}
      </nav>

      <div className="song-collections__toolbar">
        <div className="song-collections__search">
          <Search size={18} aria-hidden="true" />
          <label className="visually-hidden" htmlFor="collection-search">Search this collection</label>
          <input id="collection-search" type="search" value={query} onChange={event => setQuery(event.target.value)}
            placeholder="Search titles, composers, or voicing…" />
        </div>
        <label className="song-collections__select">Season
          <select value={season} onChange={event => setSeason(event.target.value)}>
            <option value="">All seasons</option>
            {seasons.filter(item => songs.some(song => song.tags.includes(item))).map(item =>
              <option key={item} value={item}>{item}</option>)}
          </select>
        </label>
        <label className="song-collections__select">Files
          <select value={availability} onChange={event => setAvailability(event.target.value)}>
            <option value="">All pieces</option><option value="scores">Scores available</option>
            <option value="sources">Editable files only</option>
          </select>
        </label>
        {isAdmin && <Link to="/browse?tab=songs">Organize an existing song</Link>}
      </div>

      {loading ? <p role="status" className="p-4">Loading collections…</p> :
        error ? <div role="alert" className="alert alert-danger">{error}
          <button type="button" className="btn btn-outline-danger btn-sm ms-3" onClick={() => setRetry(value => value + 1)}>Try again</button>
        </div> : <>
          <p className="text-muted small" role="status">{visible.length} {visible.length === 1 ? 'piece' : 'pieces'}{collection ? ` in ${collection.label}` : ''}</p>
          {visible.length ? <div className="song-collections__grid">
            {visible.map(song => <article className="song-collections__card" key={song.song_id}>
              <div className="song-collections__labels">
                {SONG_COLLECTIONS.filter(item => isInCollection(song.tags, item.tag)).map(item =>
                  <span key={item.id}>{item.singular}</span>)}
              </div>
              <h2><Link to={`/browse?tab=songs&songId=${song.song_id}`}>{song.title}</Link></h2>
              {song.composer_name && <p className="text-muted">{song.composer_name}</p>}
              <CollectionRating songId={song.song_id} title={song.title} rating={ratings[song.song_id] ?? null}
                onSaved={rating => setRatings(current => ({ ...current, [song.song_id]: rating }))} />
              <div className="song-collections__tags">
                {song.tags.filter(tag => !SONG_COLLECTIONS.some(item => isInCollection([tag], item.tag)))
                  .map(tag => <span key={tag}>{tag}</span>)}
              </div>
              {!hasScore(song) && hasArchive(song) && <p className="song-collections__source-note">Editable notation collected. PDF export still needed.</p>}
              {song.versions.slice(0, 1).map(version => renderVersion(song, version))}
              {song.versions.length > 1 && <details className="song-collections__alternates">
                <summary>{song.versions.length - 1} more {song.versions.length === 2 ? 'part or version' : 'parts and versions'}</summary>
                {song.versions.slice(1).map(version => renderVersion(song, version))}
              </details>}
              {!song.versions.length && <p className="text-muted">No score attached yet.</p>}
              <Link className="song-collections__open" to={`/browse?tab=songs&songId=${song.song_id}`}>
                <BookOpen size={16} aria-hidden="true" /> {isAdmin ? 'Edit title, upload or delete' : 'Open song'}
              </Link>
            </article>)}
          </div> : <div className="song-collections__empty">
            <BookOpen size={32} aria-hidden="true" />
            <h2>{query || season || availability ? 'No matching pieces' : `No ${collection?.label.toLowerCase() || 'pieces'} yet`}</h2>
            <p>{query || season || availability ? 'Try another title, season, or file filter.' : 'This collection is ready for its first song.'}</p>
            {query || season || availability ? <button type="button" className="btn btn-outline-secondary" onClick={() => { setQuery(''); setSeason(''); setAvailability(''); }}>Clear search</button> :
              isAdmin && <Link className="btn btn-outline-primary" to={addUrl}>Add the first song</Link>}
          </div>}
          {isAdmin && <p className="song-collections__hint">To include an existing piece, open its song record, choose a collection, and save.</p>}
        </>}

      <Modal show={!!preview} onHide={() => setPreview(null)} size="xl" centered className="song-score-preview">
        <Modal.Header closeButton><Modal.Title>{preview?.title}</Modal.Title></Modal.Header>
        <Modal.Body>{preview && (preview.file.extension.toLowerCase() === 'pdf' ?
          <iframe src={fileUrl(preview.file)} title={`Score: ${preview.title}`} /> :
          <img src={fileUrl(preview.file)} alt={`Score: ${preview.title}`} />)}</Modal.Body>
        <Modal.Footer>{preview && <a className="btn btn-primary" href={fileUrl(preview.file)} target="_blank" rel="noreferrer">Open score in a new tab</a>}</Modal.Footer>
      </Modal>
    </main>
  );
};
export default SongCollectionsPage;
